import useForkRef from '@mui/utils/useForkRef';
import React from 'react';
import { alpha } from '@mui/system';
import { ClickAwayListener } from '@mui/material';
import { Tooltip } from '../tooltip';
import { TooltipProps } from '../tooltip/model';
import { Typography } from '../typography';
import { useTheme } from '../../hooks/useTheme';
import { ActionButtonMenu } from './components/menu';
import { ActionButtonRoot } from './components/root';
import { ActionButtonMenuProps, ActionButtonOwnerState, ActionButtonProps } from './model';

const ActionButtonRefCombiner = React.forwardRef(
  (
    {
      children,
      additionalRef,
      extraAdditionalRef,
      ...props
    }: {
      children: React.ReactElement<any>;
      additionalRef: React.RefObject<HTMLButtonElement | null>;
      extraAdditionalRef: React.ForwardedRef<HTMLButtonElement>;
    },
    ref,
  ) => {
    const combinedAdditionalRef = useForkRef(extraAdditionalRef, additionalRef);
    const combinedRef = useForkRef(ref, combinedAdditionalRef);
    return React.cloneElement(children, { ref: combinedRef, ...props });
  },
);

export const ActionButton = React.forwardRef<HTMLButtonElement, ActionButtonProps>(
  ({ disabled = false, hint, menuItems, menuSize, renderMenu, onClick, TooltipProps: tooltipPropsRewrite, ...props }, ref) => {
    const theme = useTheme();
    const containerRef = React.useRef<HTMLButtonElement>(null);
    const [isHintVisible, setHintVisible] = React.useState(false);
    const [isMenuOpened, setMenuOpened] = React.useState(false);

    React.useEffect(() => {
      if (!hint || !containerRef.current) {
        return;
      }

      const container = containerRef.current;

      const handleMouseOver = () => setHintVisible(true);
      const handleMouseOut = () => setHintVisible(false);

      container.addEventListener('mouseover', handleMouseOver);
      container.addEventListener('mouseout', handleMouseOut);

      return () => {
        container.removeEventListener('mouseover', handleMouseOver);
        container.removeEventListener('mouseout', handleMouseOut);
      };
    }, [hint]);

    const handleClick = menuItems || renderMenu ? () => setMenuOpened(true) : onClick;

    const handleMenuClose = () => setMenuOpened(false);

    const ownerState: ActionButtonOwnerState = {
      isMenuOpened,
      disabled,
    };

    const menuProps: ActionButtonMenuProps<HTMLUListElement> = {
      items: menuItems,
      onClose: handleMenuClose,
      size: menuSize,
    };

    const tooltipProps: Omit<TooltipProps, 'children'> = {
      arrow: false,
      colorSchema: 'light',
      placement: 'top',
      disableFocusListener: true,
      disableHoverListener: true,
      disableTouchListener: true,
      componentsProps: {
        tooltip: {
          sx: {
            backgroundColor: `${theme.palette.background.default} !important`,
            boxShadow: `0px 4px 16px ${alpha(theme.palette.black as string, 0.13)} !important`,
            borderRadius: `${theme.borderRadius.normal}px !important`,
            fontSize: 'inherit !important',
            fontWeight: 'inherit !important',
            color: 'inherit !important',
            '&:focus, &:active': {
              outline: 'none !important',
            },
          },
        },
      },
      title:
        (menuItems || renderMenu) && isMenuOpened ? (
          <ClickAwayListener onClickAway={handleMenuClose}>
            {renderMenu ? renderMenu(menuProps) : <ActionButtonMenu {...menuProps} />}
          </ClickAwayListener>
        ) : (
          hint && (
            <Typography sx={{ px: 1.5, py: 0.5 }} variant="caption">
              {hint}
            </Typography>
          )
        ),
      ...tooltipPropsRewrite,
      onClose: handleMenuClose,
      open: isHintVisible || isMenuOpened,
    };

    return (
      <Tooltip {...tooltipProps}>
        <ActionButtonRefCombiner additionalRef={containerRef} extraAdditionalRef={ref}>
          {'renderButton' in props ? (
            props.renderButton({ ownerState, props: { onClick: handleClick, ...props } })
          ) : (
            <ActionButtonRoot onClick={handleClick} ownerState={ownerState} {...props}>
              {props.children}
            </ActionButtonRoot>
          )}
        </ActionButtonRefCombiner>
      </Tooltip>
    );
  },
);
